import { getApiHost } from '@/appSettings';
import { useLoginStore } from '@/login/stores/useLoginStore';

import { RestManager } from '@/newApi/services/rest/RestManager';
import type { QueryOptions, UseMutationOptions } from '@tanstack/vue-query';
import { queryOptions, useQueryClient } from '@tanstack/vue-query';
import type { PayRangeDraftTreeNode, PayRangeTreeNode, PayRangeAddOption, BulkDeletePayRangesInput, BulkAddInput, BulkPatchPayRangeAttributesInput, BulkOperationsPayRangesResponse, PayRangeActualSalary, BulkPayRangeCountrySalaryType, JobPayRangesResponse } from '@sysarb/sysarb-types-v2/services/v1/job/payRange';
import { useMockPayRanges, getMockDraftNodeOptions, getMockJobPayRanges } from '@/mocks/mockPayRanges';
import type { IsDraft } from '@sysarb/sysarb-types-v2/services/v1/job';
import type { CountryCode, EmploymentSalaryType } from '@sysarb/sysarb-types/general/enums';
import { ResgateManager, ResErr } from '@/newApi/services/ResgateManager';
export type { PayRangeAddOption, BulkPatchPayRangeAttributesInput as BulkPatchPayload, BulkPayRangeCountrySalaryType } from '@sysarb/sysarb-types-v2/services/v1/job/payRange';

const jobService = new RestManager(`https://${getApiHost()}/job-service`);

// ─── Request / Response Types ─────────────────────────────────────────────────

export type PayRangesChangesResponse = { changeID: string };
export type BulkPatchResponse = { success: boolean };
export type PayRangeNoOptionsResponse = { nodeIds: string[] };

const mock = {
    payRangeTree: false,
    payRangeOptions: false,
    payRangeNoOptions: false,
    jobPayRanges: false,
    bulkAdd: false,
    bulkDelete: false,
    bulkPatch: false,
    bulkUpdateSalaryTypes: false,
};

// ─── API Definition ───────────────────────────────────────────────────────────

type JobPayRangesOptions = {
    queries: {
        getPayRangeChangeId: () => QueryOptions<PayRangesChangesResponse, Error, PayRangesChangesResponse>;
        getPayRangeDraftChangeId: () => QueryOptions<PayRangesChangesResponse, Error, PayRangesChangesResponse>;
        getPayRangeTree: (payload: IsDraft) => QueryOptions<PayRangeDraftTreeNode[] | PayRangeTreeNode[], Error, PayRangeDraftTreeNode[] | PayRangeTreeNode[]>;
        getPayRangeActualSalaries: (payload: IsDraft) => QueryOptions<PayRangeActualSalary[], Error, PayRangeActualSalary[]>;
        getDraftNodeOptions: (nodeId: string) => QueryOptions<PayRangeAddOption[], Error, PayRangeAddOption[]>;
        getPayRangeNoOptions: () => QueryOptions<PayRangeNoOptionsResponse, Error, PayRangeNoOptionsResponse>;
        getJobPayRanges: (jobId: string, country: CountryCode, payload: IsDraft & { salaryType?: EmploymentSalaryType }) => QueryOptions<JobPayRangesResponse, Error, JobPayRangesResponse>;
    };
    mutations: {
        bulkAdd: () => UseMutationOptions<BulkOperationsPayRangesResponse | null, Error, BulkAddInput>;
        bulkDelete: () => UseMutationOptions<BulkOperationsPayRangesResponse | null, Error, BulkDeletePayRangesInput>;
        initBaseData: () => UseMutationOptions<void, Error, void>;
        bulkPatch: () => UseMutationOptions<BulkPatchResponse, Error, BulkPatchPayRangeAttributesInput>;
        bulkUpdateSalaryTypes: () => UseMutationOptions<BulkPatchResponse, Error, BulkPayRangeCountrySalaryType>;
    };
};

export const jobPayRanges: JobPayRangesOptions = {
    queries: {
        getPayRangeChangeId: (): QueryOptions<PayRangesChangesResponse, Error, PayRangesChangesResponse> => {
            const { instanceTopic } = useLoginStore();
            const topic = `v1.${instanceTopic}.jobs.pay-ranges.changes`;
            return queryOptions<PayRangesChangesResponse, Error>({
                meta: {
                    subscribe: true,
                    type: 'resgate',
                    resourceId: topic,
                },
                queryKey: ['pay-ranges-change-id', topic],
                queryFn: async (): Promise<PayRangesChangesResponse> => {
                    return ResgateManager.model<PayRangesChangesResponse>(topic).then(res => {
                        if (res instanceof ResErr) {
                            throw new Error(res.message);
                        }
                        return res;
                    });
                },
            });
        },
        getPayRangeDraftChangeId: (): QueryOptions<PayRangesChangesResponse, Error, PayRangesChangesResponse> => {
            const { instanceTopic } = useLoginStore();
            const topic = `v1.${instanceTopic}.jobs.pay-ranges-draft.changes`;
            return queryOptions<PayRangesChangesResponse, Error>({
                meta: {
                    subscribe: true,
                    type: 'resgate',
                    resourceId: topic,
                },
                queryKey: ['pay-ranges-draft-change-id', topic],
                queryFn: async (): Promise<PayRangesChangesResponse> => {
                    return ResgateManager.model<PayRangesChangesResponse>(topic).then(res => {
                        if (res instanceof ResErr) {
                            throw new Error(res.message);
                        }
                        return res;
                    });
                },
            });
        },
        getPayRangeTree: (payload: IsDraft) => {
            const { payRangeRows } = useMockPayRanges();
            const { instance } = useLoginStore();
            const path = payload.draft
                ? `v1/${instance}/pay-ranges/draft/tree`
                : `v1/${instance}/pay-ranges/tree`;
            return queryOptions<PayRangeDraftTreeNode[] | PayRangeTreeNode[], Error, PayRangeDraftTreeNode[] | PayRangeTreeNode[]>({
                queryKey: ['pay-ranges-tree', instance, payload.draft, path, mock.payRangeTree ? payRangeRows.value : null],
                queryFn: async (): Promise<PayRangeDraftTreeNode[] | PayRangeTreeNode[]> => {
                    if (mock.payRangeTree) {
                        return payRangeRows.value;
                    }
                    if (payload.draft) {
                        const draftResult = await jobService.get<PayRangeDraftTreeNode[]>(path);
                        return draftResult;
                    }
                    return jobService.get<PayRangeTreeNode[]>(path);
                },
                select: (data) => data,
            });
        },

        getPayRangeActualSalaries: (payload: IsDraft): QueryOptions<PayRangeActualSalary[], Error, PayRangeActualSalary[]> => {
            const { instance } = useLoginStore();
            const path = payload.draft
                ? `v1/${instance}/pay-ranges/draft/tree/actual-salaries`
                : `v1/${instance}/pay-ranges/tree/actual-salaries`;
            return queryOptions<PayRangeActualSalary[], Error, PayRangeActualSalary[]>({
                queryKey: ['pay-ranges-actual-salaries', instance, payload.draft, path],
                queryFn: async (): Promise<PayRangeActualSalary[]> => jobService.get<PayRangeActualSalary[]>(path),
            });
        },

        getDraftNodeOptions: (nodeId: string): QueryOptions<PayRangeAddOption[], Error, PayRangeAddOption[]> => {
            const { payRangeRows } = useMockPayRanges();
            const { instance } = useLoginStore();
            return queryOptions<PayRangeAddOption[], Error, PayRangeAddOption[]>({
                queryKey: ['pay-ranges-draft-node-options', instance, nodeId, mock.payRangeOptions ? payRangeRows.value : null],
                queryFn: async (): Promise<PayRangeAddOption[]> => {
                    if (mock.payRangeOptions) {
                        return getMockDraftNodeOptions(nodeId, payRangeRows.value);
                    }
                    const optionsPath = `v1/${instance}/pay-ranges/draft/${nodeId}/options`;
                    return jobService.get<PayRangeAddOption[]>(optionsPath);
                },
            });
        },

        /**
         * The nodes whose add menu would come back empty, so their "+" can render disabled without
         * opening the menu first.
         */
        getPayRangeNoOptions: (): QueryOptions<PayRangeNoOptionsResponse, Error, PayRangeNoOptionsResponse> => {
            const { instance } = useLoginStore();
            const path = `v1/${instance}/pay-ranges/draft/no-options`;
            return queryOptions<PayRangeNoOptionsResponse, Error, PayRangeNoOptionsResponse>({
                queryKey: ['pay-ranges-no-options', instance, path],
                queryFn: async (): Promise<PayRangeNoOptionsResponse> => {
                    if (mock.payRangeNoOptions) {
                        return { nodeIds: [] };
                    }
                    return jobService.get<PayRangeNoOptionsResponse>(path);
                },
            });
        },

        /**
         * The pay range nodes the job's active employees are assigned to in a given country,
         * together with an aggregated range (lowest min, highest max) across those nodes.
         * When salaryType is given, min and max are converted to that salary type by the backend.
         */
        getJobPayRanges: (jobId: string, country: CountryCode, payload: IsDraft & { salaryType?: EmploymentSalaryType }): QueryOptions<JobPayRangesResponse, Error, JobPayRangesResponse> => {
            const { payRangeRows } = useMockPayRanges();
            const { instance } = useLoginStore();
            const { salaryType, ...isDraft } = payload;
            const query = salaryType != null ? `?salaryType=${salaryType}` : '';
            const path = `v1/${instance}/job/${jobId}/pay-ranges/${country}${query}`;
            return queryOptions<JobPayRangesResponse, Error, JobPayRangesResponse>({
                queryKey: ['job-pay-ranges', instance, jobId, country, path, isDraft, mock.jobPayRanges ? payRangeRows.value : null],
                queryFn: async (): Promise<JobPayRangesResponse> => {
                    if (mock.jobPayRanges) {
                        return getMockJobPayRanges(country, payRangeRows.value);
                    }
                    return jobService.post<JobPayRangesResponse>(isDraft, path);
                },
            });
        },
    },

    mutations: {
        bulkAdd: (): UseMutationOptions<BulkOperationsPayRangesResponse | null, Error, BulkAddInput> => {
            const { bulkAddNodes } = useMockPayRanges();
            const queryClient = useQueryClient();
            return {
                mutationFn: async (payload: BulkAddInput): Promise<BulkOperationsPayRangesResponse | null> => {
                    if (mock.bulkAdd) {
                        const added = bulkAddNodes(payload);
                        return { nodeIds: added.map(x => x.id) };
                    }
                    const { instance } = useLoginStore();
                    return jobService.post<BulkOperationsPayRangesResponse>(payload, `/v1/${instance}/pay-ranges/draft/tree`);
                },
                onSettled: async (): Promise<void> => {
                    await queryClient.invalidateQueries({ queryKey: ['pay-ranges-tree'] });
                    await queryClient.invalidateQueries({ queryKey: ['pay-ranges-actual-salaries'] });
                    await queryClient.invalidateQueries({ queryKey: ['pay-ranges-draft-node-options'] });
                    await queryClient.invalidateQueries({ queryKey: ['pay-ranges-no-options'] });
                },
            };
        },

        bulkPatch: (): UseMutationOptions<BulkPatchResponse, Error, BulkPatchPayRangeAttributesInput> => {
            const { bulkPatchNodes } = useMockPayRanges();
            const queryClient = useQueryClient();
            return {
                mutationFn: async (payload: BulkPatchPayRangeAttributesInput): Promise<BulkPatchResponse> => {
                    if (mock.bulkPatch) {
                        bulkPatchNodes(payload);
                        return { success: true };
                    }
                    const { instance } = useLoginStore();
                    return jobService.post<BulkPatchResponse>(payload, `/v1/${instance}/pay-ranges/draft/`);
                },
                onSettled: async (): Promise<void> => {
                    await queryClient.invalidateQueries({ queryKey: ['pay-ranges-tree'] });
                },
            };
        },
        bulkDelete: (): UseMutationOptions<BulkOperationsPayRangesResponse | null, Error, BulkDeletePayRangesInput> => {
            const { bulkDeleteNodes } = useMockPayRanges();
            const queryClient = useQueryClient();
            return {
                mutationFn: async (payload: BulkDeletePayRangesInput): Promise<BulkOperationsPayRangesResponse | null> => {
                    if (mock.bulkDelete) {
                        bulkDeleteNodes(payload);
                        return { nodeIds: [] };
                    }
                    const { instance } = useLoginStore();
                    return jobService.delete<BulkOperationsPayRangesResponse>(`/v1/${instance}/pay-ranges/draft/tree`, undefined, { data: payload });
                },
                onSettled: async (): Promise<void> => {
                    await queryClient.invalidateQueries({ queryKey: ['pay-ranges-tree'] });
                    await queryClient.invalidateQueries({ queryKey: ['pay-ranges-draft-node-options'] });
                    await queryClient.invalidateQueries({ queryKey: ['pay-ranges-no-options'] });
                },
            };
        },
        initBaseData: (): UseMutationOptions<void, Error, void> => {
            const queryClient = useQueryClient();
            return {
                mutationFn: async (): Promise<void> => {
                    const { instance } = useLoginStore();
                    await jobService.post<void>({}, `v1/${instance}/pay-ranges/draft/init`);
                },
                onSettled: async (): Promise<void> => {
                    await queryClient.invalidateQueries({ queryKey: ['pay-ranges-tree'] });
                    await queryClient.invalidateQueries({ queryKey: ['pay-ranges-actual-salaries'] });
                    await queryClient.invalidateQueries({ queryKey: ['pay-ranges-no-options'] });
                },
            };
        },
        bulkUpdateSalaryTypes: (): UseMutationOptions<BulkPatchResponse, Error, BulkPayRangeCountrySalaryType> => {
            const queryClient = useQueryClient();
            return {
                mutationFn: async (payload: BulkPayRangeCountrySalaryType): Promise<BulkPatchResponse> => {
                    if (mock.bulkUpdateSalaryTypes) {
                        return { success: true };
                    }
                    const { instance } = useLoginStore();
                    return jobService.post<BulkPatchResponse>(payload, `/v1/${instance}/pay-ranges/draft/salary-type`);
                },
                onSuccess: (_data, payload): void => {
                    const salaryTypeByCountry = new Map(payload.map((row) => [row.country, row.salaryType]));
                    const { queryKey } = jobPayRanges.queries.getPayRangeTree({ draft: true });
                    queryClient.setQueryData<PayRangeDraftTreeNode[] | PayRangeTreeNode[]>(
                        queryKey,
                        (nodes) => nodes?.map((node) => {
                            const salaryType = salaryTypeByCountry.get(node.country);
                            return salaryType != null && salaryType !== node.salaryType ? { ...node, salaryType } : node;
                        }),
                    );
                    // The pay range values the user typed in are left as they are, but the actual salaries
                    // are aggregated in the country's salary type, so they have to be recalculated server-side.
                    void queryClient.invalidateQueries({ queryKey: ['pay-ranges-actual-salaries'] });
                },
                onError: (): void => {
                    const { queryKey } = jobPayRanges.queries.getPayRangeTree({ draft: true });
                    void queryClient.invalidateQueries({ queryKey });
                },
            };
        },
    },
};
